import ClipperLib from 'clipper-lib';
import type {
  Path as IntPath,
  PolyTree,
  ClipType as IntClipType,
  PolyFillType as IntFillType,
  JoinType as IntJoinType,
  EndType as IntEndType,
} from 'clipper-lib';
import { CLIPPER_SCALE } from '../core/config';
import type { Vec2, Ring, PolygonWithHoles } from './types';
import { ensureCCW, ensureCW } from './polygon';
import { simplifyRing } from './simplifyRing';

export type FillRule = 'nonzero' | 'evenodd';
export type JoinKind = 'miter' | 'round' | 'square';

// Every boolean and offset runs on Clipper 6.4.2 (clipper-lib). clipper2-js 1.2.4 was dropped: its
// offsetter builds each join against the wrong edge (`k = j`, IRobot1/clipper2-ts#3), its
// intersection returned more than its subject where two outlines nearly coincide, and its
// simplifyPath at 0.1 m left a ground outline 2 km from the original.
const MITER_LIMIT = 2;
// Chord error of round joins and caps; the site partition simplifies at 0.1 m, so finer arcs only add vertices.
const ARC_TOLERANCE_M = 0.005;

function toIntPath(ring: readonly Vec2[]): IntPath {
  return ring.map((p) => ({
    X: Math.round(p[0] * CLIPPER_SCALE),
    Y: Math.round(p[1] * CLIPPER_SCALE),
  }));
}

function toIntPaths(polys: readonly PolygonWithHoles[]): IntPath[] {
  return polys.flatMap((poly) => [poly.outer, ...poly.holes]).map(toIntPath);
}

function fromIntPath(path: IntPath): Ring {
  return path.map((p): Vec2 => [p.X / CLIPPER_SCALE, p.Y / CLIPPER_SCALE]);
}

// The PolyTree nests every hole under its own outer, so no containment guess is needed.
function fromTree(tree: PolyTree): readonly PolygonWithHoles[] {
  return ClipperLib.JS.PolyTreeToExPolygons(tree)
    .filter((ex) => ex.outer.length >= 3)
    .map((ex) => ({
      outer: fromIntPath(ex.outer),
      holes: ex.holes.filter((hole) => hole.length >= 3).map(fromIntPath),
    }));
}

function toFillType(rule: FillRule): IntFillType {
  return rule === 'nonzero'
    ? ClipperLib.PolyFillType.pftNonZero
    : ClipperLib.PolyFillType.pftEvenOdd;
}

function execute(
  clipType: IntClipType,
  subject: readonly PolygonWithHoles[],
  clip: readonly PolygonWithHoles[],
  fill: FillRule
): readonly PolygonWithHoles[] {
  const clipper = new ClipperLib.Clipper();
  // Clipper's default output may join two parts that meet along an edge into one ring that runs
  // along it twice (the polder either side of a dike at the bounds): earcut cannot read such a ring
  // and the far ring meets it with a T-junction. Differences and intersections cut every outline
  // that is triangulated, so they come out strictly simple; unions only feed further cuts and
  // keep-out tests, and skip the extra pass, which would add a third to the site stage.
  clipper.StrictlySimple = clipType !== ClipperLib.ClipType.ctUnion;
  clipper.AddPaths(toIntPaths(subject), ClipperLib.PolyType.ptSubject, true);
  clipper.AddPaths(toIntPaths(clip), ClipperLib.PolyType.ptClip, true);
  const tree = new ClipperLib.PolyTree();
  clipper.Execute(clipType, tree, toFillType(fill), toFillType(fill));
  return fromTree(tree);
}

function toJoinType(join: JoinKind): IntJoinType {
  switch (join) {
    case 'miter':
      return ClipperLib.JoinType.jtMiter;
    case 'round':
      return ClipperLib.JoinType.jtRound;
    case 'square':
      return ClipperLib.JoinType.jtSquare;
  }
}

function offsetPaths(
  paths: readonly IntPath[],
  delta: number,
  join: IntJoinType,
  end: IntEndType
): readonly PolygonWithHoles[] {
  const offsetter = new ClipperLib.ClipperOffset(MITER_LIMIT, ARC_TOLERANCE_M * CLIPPER_SCALE);
  offsetter.AddPaths([...paths], join, end);
  const tree = new ClipperLib.PolyTree();
  offsetter.Execute(tree, delta * CLIPPER_SCALE);
  return fromTree(tree);
}

export function fromRing(r: Ring): PolygonWithHoles {
  return { outer: r, holes: [] };
}

export function union(
  polys: readonly PolygonWithHoles[],
  fill: FillRule = 'nonzero'
): readonly PolygonWithHoles[] {
  return execute(ClipperLib.ClipType.ctUnion, polys, [], fill);
}

export function unionTwo(
  a: readonly PolygonWithHoles[],
  b: readonly PolygonWithHoles[],
  fill: FillRule = 'nonzero'
): readonly PolygonWithHoles[] {
  return execute(ClipperLib.ClipType.ctUnion, a, b, fill);
}

export function difference(
  subject: readonly PolygonWithHoles[],
  clip: readonly PolygonWithHoles[]
): readonly PolygonWithHoles[] {
  return execute(ClipperLib.ClipType.ctDifference, subject, clip, 'nonzero');
}

export function intersection(
  subject: readonly PolygonWithHoles[],
  clip: readonly PolygonWithHoles[]
): readonly PolygonWithHoles[] {
  return execute(ClipperLib.ClipType.ctIntersection, subject, clip, 'nonzero');
}

// Clipper takes the lowest ring's winding as the outer winding, so rings are normalised first.
export function inflate(
  polys: readonly PolygonWithHoles[],
  delta: number,
  join: JoinKind
): readonly PolygonWithHoles[] {
  const paths = polys.flatMap((poly) => [
    toIntPath(ensureCCW(poly.outer)),
    ...poly.holes.map((hole) => toIntPath(ensureCW(hole))),
  ]);
  return offsetPaths(paths, delta, toJoinType(join), ClipperLib.EndType.etClosedPolygon);
}

export function bufferPolyline(
  pts: readonly Vec2[],
  halfWidth: number,
  ends: 'round' | 'butt'
): readonly PolygonWithHoles[] {
  if (pts.length < 2) return [];
  const end = ends === 'round' ? ClipperLib.EndType.etOpenRound : ClipperLib.EndType.etOpenButt;
  return offsetPaths([toIntPath(pts)], halfWidth, ClipperLib.JoinType.jtRound, end);
}

export function simplify(
  polys: readonly PolygonWithHoles[],
  tolerance: number
): readonly PolygonWithHoles[] {
  return polys.map((poly) => ({
    outer: simplifyRing(poly.outer, tolerance),
    holes: poly.holes.map((hole) => simplifyRing(hole, tolerance)),
  }));
}
